import { ROOT_RING_REST } from '../../models/rootking.ts';
import { clip, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inExpo, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';

/*
 * El Raigón (models/rootking.ts): an old trunk that walks. Heavy like the frost colossus (whose clips it shares
 * the shape of), but stiff in the trunk and loose in the branches: it rolls over each root-foot, the claws drag.
 *
 * Conventions as in human.ts: −X on an arm or leg swings it forward (up, overhead), +X on a shin bends the knee,
 * −X on a forearm bends the elbow; +X on the trunk or the head leans it forward; +Z on the right arm (+X) lifts
 * it out to the side, −Z the left. `ring` (the circle of roots) is built tiny at the ground: only 'plunge' and
 * 'rooted' scale it up (1 / ROOT_RING_REST); every other clip leaves it hidden.
 *
 * Blows: attack (a swipe of the right claw) lands at 0.13 s. The rule: 'plunge' (3.0 s): rears with both claws
 * high (0.7 s), drives them into the ground (0.95 s), the ring bursts up (0.9–1.3 s), squeezes, lets go (2.55 s).
 * 'rooted' (1.6 s loop) is the hold, for a hold longer than the plunge's.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'ring'
  | 'armL' | 'foreL' | 'handL' | 'armR' | 'foreR' | 'handR'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const RING = 1 / ROOT_RING_REST;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

const idle = (): Clip<B> =>
  clip<B>('idle', 4.5, true, (b, t) => {
    const p = phase(t, 4.5) * TAU;
    const br = Math.sin(p * 2);
    b.root.pos(0, -0.012 * (1 + br), 0).rot(0, 0, 0.012 * Math.sin(p));
    b.body.rot(0.01 * br, 0, 0.015 * Math.sin(p));
    // The great chest heaves; the shoulders rise with it.
    b.chest.rot(-0.035 * br, 0.05 * Math.sin(p), 0.02 * Math.sin(p + 0.4)).scale(1 + 0.008 * br);
    b.head.rot(0.04 * br, 0.28 * Math.sin(p + 0.6), 0.05 * Math.sin(p));
    b.armL.rot(0.04 * Math.sin(p + 0.3), 0, -0.03 * br);
    b.armR.rot(0.04 * Math.sin(p + 1.3), 0, 0.03 * br);
    b.foreL.rot(-0.04 * Math.sin(p + 0.8));
    b.foreR.rot(-0.04 * Math.sin(p + 1.8));
    b.handL.rot(0.08 * Math.sin(p * 2 + 1));
    b.handR.rot(0.08 * Math.sin(p * 2 + 2.4));
  }, (t) => {
    const br = Math.sin(phase(t, 4.5) * TAU * 2);
    return [1 + 0.008 * br, 1 - 0.006 * br, 1 + 0.008 * br];
  });

const joltAt = (t: number, dur: number, freq: number, damp: number): number => {
  const half = dur / 2;
  return settle((((t - dur / 4) % half) + half) % half, freq, damp);
};

/** The lumber: short heavy strides, the whole mass rolling over each planted foot, the arms swinging like bells. */
const walk = (dur = 2.6): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const jolt = Math.max(0, joltAt(t, dur, 2.0, 6));
    const A = 0.3;
    b.legL.rot(-A * sn - 0.04, 0, -0.03);
    b.legR.rot(A * sn - 0.04, 0, 0.03);
    b.shinL.rot(0.6 * Math.max(0, cs) ** 1.5 + 0.05);
    b.shinR.rot(0.6 * Math.max(0, -cs) ** 1.5 + 0.05);
    b.root.rot(0.02 * jolt, 0.05 * sn, -0.06 * cs).pos(-0.05 * cs, 0.025 * Math.cos(2 * th) - 0.03 - 0.04 * jolt, 0);
    b.body.rot(0.06 + 0.03 * jolt, 0.08 * sn, 0.03 * cs);
    b.chest.rot(0.05 + 0.03 * Math.cos(2 * th - 0.7), -0.14 * sn, 0.04 * cs);
    b.head.rot(-0.08 - 0.05 * jolt, 0.1 * sn, -0.03 * cs);
    const sL = Math.sin(th - 0.4);
    b.armL.rot(0.32 * sL, 0, -0.04);
    b.armR.rot(-0.32 * sL, 0, 0.04);
    b.foreL.rot(-0.2 * Math.max(0, -Math.sin(th - 0.8)) + 0.05 * jolt);
    b.foreR.rot(-0.2 * Math.max(0, Math.sin(th - 0.8)) + 0.05 * jolt);
    b.handL.rot(0.25 * Math.sin(th - 1.1));
    b.handR.rot(-0.25 * Math.sin(th - 1.1));
  }, (t) => {
    const j = Math.max(0, joltAt(t, dur, 2.0, 9));
    return [1 + 0.03 * j, 1 - 0.04 * j, 1 + 0.03 * j];
  });

/** Raised: the right fist up over its shoulder, the trunk reared and turned. */
const RAISED: P = {
  body: [-0.06, -0.12, 0],
  chest: [-0.2, -0.3, 0.05],
  head: [-0.12, 0.25, 0],
  armR: [-2.5, 0, 0.35],
  foreR: [-1.0, 0, 0],
  handR: [0.3, 0, 0],
  armL: [-0.3, 0, -0.25],
  foreL: [-0.3, 0, 0],
  legR: [0.15, 0, 0.04],
  shinR: [0.2, 0, 0],
  legL: [-0.2, 0, -0.04],
  shinL: [0.2, 0, 0],
};
/** The hammer: down through its target, the trunk folding after it. */
const HAMMER: P = {
  root: [0, 0, 0, 0, -0.08, 0.1],
  body: [0.2, 0.12, 0],
  chest: [0.4, 0.3, -0.05],
  head: [0.05, -0.2, 0],
  armR: [-0.7, 0, 0.1],
  foreR: [-0.15, 0, 0],
  handR: [-0.2, 0, 0],
  armL: [0.2, 0, -0.3],
  foreL: [-0.2, 0, 0],
  legR: [0.25, 0, 0.04],
  shinR: [0.25, 0, 0],
  legL: [-0.42, 0, -0.04],
  shinL: [0.4, 0, 0],
};

const attack = (): Clip<B> =>
  clip<B>('attack', 1.5, false, (b, t) => {
    timeline(b, t, [
      [0, RAISED],
      [0.05, { ...RAISED, armR: [-2.62, 0, 0.38], chest: [-0.24, -0.34, 0.05] }, outQuad],
      [0.13, HAMMER, inExpo],
      [0.45, { ...HAMMER, chest: [0.38, 0.28, -0.05], root: [0, 0, 0, 0, -0.075, 0.1] }, outCubic],
      [1.5, {}, inOut],
    ], { head: 0.04, handR: 0.03 });
    b.root.pos(0.014 * kick(t, 0.13, 7, 8), 0, 0);
  }, (t) => thud(t, [[0.13, 0.05]]));

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.0, 8) * span(t, 0, 0.04, outQuad);
    b.chest.rot(-0.08 * q, 0.05 * q);
    b.body.rot(-0.03 * q);
    b.head.rot(-0.18 * q, -0.08 * q, 0.05 * q);
    b.root.pos(0, 0, -0.03 * q);
    b.armL.rot(0.1 * q, 0, -0.05 * q);
    b.armR.rot(0.1 * q, 0, 0.05 * q);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2.8, true, (b, t) => {
    const p = phase(t, 2.8) * TAU;
    b.root.pos(0, -0.06, 0).rot(0.02 * Math.sin(p), 0, 0.04 * Math.cos(p));
    b.body.rot(0.2, 0, 0);
    b.chest.rot(0.25 + 0.04 * Math.sin(p * 2), 0.08 * Math.sin(p), -0.05 * Math.cos(p));
    b.head.rot(0.4 + 0.12 * Math.sin(p + 0.8), 0.28 * Math.cos(p + 0.8), 0.2 * Math.sin(p + 0.8));
    b.armL.rot(0.1 * Math.sin(p + 1.4), 0, 0.05);
    b.armR.rot(-0.1 * Math.sin(p + 1.4), 0, -0.05);
    b.legL.rot(-0.15);
    b.shinL.rot(0.3);
    b.legR.rot(-0.12);
    b.shinR.rot(0.28 + 0.06 * Math.sin(p));
  });

/** The fall (3 s): it rears with a soundless roar, its knees go (1.3 s), it sways, and topples onto its face (2.4 s). */
const die = (): Clip<B> =>
  clip<B>('die', 3.0, false, (b, t) => {
    const kneel: P = { legL: [0.0, 0, -0.1], shinL: [1.5, 0, 0], legR: [0.05, 0, 0.1], shinR: [1.5, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.35, { root: [0, 0, 0, 0, 0.02, -0.06], body: [-0.15, 0.1, 0], chest: [-0.3, 0.12, 0], head: [-0.6, 0.2, 0.1], armL: [-1.6, 0, 0.6], foreL: [-0.4, 0, 0], armR: [-1.4, 0, -0.7], foreR: [-0.4, 0, 0] }, outExpo],
      [0.9, { root: [0, 0, 0.04, 0, -0.1, 0.04], body: [0.3, -0.1, 0.06], chest: [0.3, -0.1, 0], head: [0.4, 0, 0.2], armL: [-0.3, 0, -0.1], foreL: [-0.2, 0, 0], armR: [-0.35, 0, 0.1], foreR: [-0.2, 0, 0], legL: [0.2, 0, -0.04], shinL: [0.4, 0, 0], legR: [-0.3, 0, 0.04], shinR: [0.45, 0, 0] }, inOut],
      [1.3, { ...kneel, root: [0, 0, 0, 0, -0.42, -0.04], body: [0.25, 0, 0.05], chest: [0.25, 0, 0], head: [0.5, 0, 0.25], armL: [-0.1, 0, -0.1], armR: [-0.1, 0, 0.1] }, inQuad],
      [1.45, { ...kneel, root: [0, 0, 0, 0, -0.4, -0.04], body: [0.3, 0, 0.05], chest: [0.28, 0, 0], head: [0.6, 0, 0.28], armL: [-0.08, 0, -0.1], armR: [-0.08, 0, 0.1] }, outBack],
      [1.95, { ...kneel, root: [0, 0, 0, 0, -0.42, -0.04], body: [0.45, 0.1, 0.1], chest: [0.35, 0.05, 0.05], head: [0.8, 0.2, 0.4], armL: [0.1, 0, -0.05], armR: [0.12, 0, 0.08] }, inOut],
      [2.4, { ...kneel, root: [0, 0, 0, 0, -0.5, 0.06], body: [1.3, 0.1, 0.12], chest: [0.3, 0.05, 0.05], head: [0.2, 0.5, 0.6], armL: [-2.2, 0, -0.5], foreL: [-0.05, 0, 0], armR: [-2.1, 0, 0.55], foreR: [-0.05, 0, 0] }, inCubic],
      [3.0, { ...kneel, root: [0, 0, 0, 0, -0.5, 0.06], body: [1.26, 0.1, 0.12], chest: [0.28, 0.05, 0.05], head: [0.3, 0.6, 0.7], armL: [-2.25, 0, -0.52], foreL: [0.02, 0, 0], armR: [-2.15, 0, 0.57], foreR: [0.02, 0, 0] }, outBack],
    ], { head: 0.06, foreL: 0.04, foreR: 0.04 });
    b.root.pos(0.015 * kick(t, 1.3, 7, 8) + 0.02 * kick(t, 2.4, 7, 8), 0, 0);
  }, (t) => thud(t, [[1.3, 0.05], [2.4, 0.08]]));

const UP: P = { root: [0, 0, 0, 0, 0.06, -0.06], body: [-0.12, 0, 0], chest: [-0.32, 0, 0], head: [-0.4, 0, 0], armL: [-2.9, 0, 0.35], foreL: [-0.5, 0, 0], handL: [0.3, 0, 0], armR: [-2.9, 0, -0.35], foreR: [-0.5, 0, 0], handR: [0.3, 0, 0], legL: [-0.1, 0, -0.05], shinL: [0.1, 0, 0], legR: [-0.1, 0, 0.05], shinR: [0.1, 0, 0] };
/** Down on its knees, both claws driven into the ground before it. */
const SUNK: P = { root: [0, 0, 0, 0, -0.32, 0.12], body: [0.42, 0, 0], chest: [0.45, 0, 0], head: [-0.15, 0, 0], armL: [-0.75, 0, 0.3], foreL: [-0.05, 0, 0], handL: [-0.35, 0, 0], armR: [-0.75, 0, -0.3], foreR: [-0.05, 0, 0], handR: [-0.35, 0, 0], legL: [-0.5, 0, -0.08], shinL: [0.85, 0, 0], legR: [-0.25, 0, 0.08], shinR: [0.75, 0, 0] };

/** The squeeze while it holds (at angle `a`, one wrench a turn): the claws wrench, the trunk strains, the ring heaves. */
const squeeze = (b: Parameters<Clip<B>['pose']>[0], a: number, on: number): void => {
  const w = Math.sin(a);
  b.handL.rot(0.12 * w * on, 0.15 * w * on, 0);
  b.handR.rot(-0.12 * w * on, -0.15 * w * on, 0);
  b.chest.rot(0.03 * Math.sin(2 * a) * on, 0.06 * Math.sin(a + 0.5) * on, 0);
  b.head.rot(-0.08 * on, 0.1 * Math.sin(a + 1) * on, 0);
  b.ring.rot(0, 0.04 * Math.sin(a) * on, 0);
};

/** The rule: rears up with both claws high, drives them into the ground, the circle of roots bursts up round it and squeezes, then it tears them free. */
const plunge = (): Clip<B> =>
  clip<B>('plunge', 3.0, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.55, UP, inOut],
      [0.7, { ...UP, chest: [-0.38, 0, 0], armL: [-3.0, 0, 0.35], armR: [-3.0, 0, -0.35] }, outQuad],
      [0.95, SUNK, inExpo],
      [1.25, { ...SUNK, root: [0, 0, 0, 0, -0.3, 0.12], chest: [0.42, 0, 0] }, outBack],
      [2.5, { ...SUNK, chest: [0.48, 0, 0] }],
      [2.75, { ...SUNK, root: [0, 0, 0, 0, -0.2, 0.06], chest: [0.1, 0, 0], body: [0.2, 0, 0], armL: [-1.2, 0, 0.3], armR: [-1.2, 0, -0.3], handL: [0.1, 0, 0], handR: [0.1, 0, 0] }, outCubic],
      [3.0, {}, inOut],
    ], { head: 0.05, handL: 0.03, handR: 0.03 });
    squeeze(b, t * 9, envelope(t, 1.25, 1.4, 2.4, 2.5));
    b.root.pos(0.02 * kick(t, 0.95, 7, 7), 0, 0);
    // The ring: up from the ground as the claws go in, gone again as they come out. It stays where it rose
    // (the root's drop and lean taken back off it).
    const up = span(t, 0.88, 1.3, outBack) * (1 - span(t, 2.55, 2.9, inQuad));
    const ry = keysRoot(t);
    b.ring.pos(0, -ry[0], -ry[1]).scale(1 + (RING - 1) * Math.max(0.001, up) * (1 + 0.03 * Math.sin(t * 6) * envelope(t, 1.3, 1.4, 2.4, 2.55)));
  }, (t) => thud(t, [[0.95, 0.1]]), 'Hunde las raíces');

/** The root bone's drop and lean through the plunge (to keep the ring on the ground). */
const keysRoot = (t: number): [number, number] => {
  const down = span(t, 0.7, 0.95, inExpo) * (1 - span(t, 2.5, 3.0, inOut));
  const rear = span(t, 0, 0.55, inOut) * (1 - span(t, 0.7, 0.95, inExpo));
  return [0.06 * rear - 0.32 * down, -0.06 * rear + 0.12 * down];
};

/** The hold as a loop: on its knees, claws in the ground, the ring up and squeezing. */
const rooted = (): Clip<B> =>
  clip<B>('rooted', 1.6, true, (b, t) => {
    timeline(b, t, [[0, { ...SUNK, chest: [0.46, 0, 0] }]]);
    const tt = phase(t, 1.6) * Math.PI * 2;
    squeeze(b, tt * 2, 1);
    b.ring.pos(0, 0.32, -0.12).scale(RING * (1 + 0.02 * Math.sin(tt)));
  }, undefined, 'Raíces hundidas');

/** Clips by library kind (see ../rigs.ts). */
export const rootkingClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), plunge(), rooted()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { rootking: rootkingClips };

